import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于bb体育',
  description: 'bb体育是您值得信赖的在线体育平台，致力于为广大体育爱好者带来极致的体育娱乐体验。了解bb体育的品牌故事、平台信誉和联系方式。',
  url: 'https://www.bbsports.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: 'bb体育',
    url: 'https://www.bbsports.com',
  },
  about: {
    '@type': 'Organization',
    name: 'bb体育',
    url: 'https://www.bbsports.com',
    description: 'bb体育是您值得信赖的在线体育平台，提供丰富的体育赛事资讯、赛事直播、体育竞技分析及相关服务。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-8888',
      contactType: 'customer service',
      email: 'service@bbsports.com',
      areaServed: 'CN',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '世纪大道100号',
      addressLocality: '上海',
      addressRegion: '浦东新区',
      addressCountry: 'CN',
    },
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bbsports.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.bbsports.com/about' },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'bb体育平台如何注册账号？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '注册bb体育账号非常简单。点击页面右上角的"注册"按钮，填写您的手机号码或邮箱地址，设置安全密码，完成短信或邮箱验证后即可成功注册。整个过程仅需2分钟。',
      },
    },
    {
      '@type': 'Question',
      name: 'bb体育平台的安全性如何保障？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'bb体育采用国际顶尖的256位SSL加密技术，严格遵循国际数据保护法规，所有用户数据均经过多重加密存储。平台持有正规运营牌照，定期接受第三方安全审计。',
      },
    },
  ],
};

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash === '#contact') {
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  }, [location.hash]);

  return (
    <>
      <SEOHead
        title="关于bb体育 - 品牌介绍、平台信誉与联系方式"
        description="了解bb体育的品牌故事与发展历程。bb体育是您值得信赖的在线体育平台，提供安全的体育赛事资讯与娱乐服务。查看平台信誉、资质认证及联系方式。"
        keywords="bb体育,关于bb体育,bb体育平台,bb体育信誉,bb体育官网,联系方式,体育平台"
        canonicalUrl="https://www.bbsports.com/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema, ...faqSchema }}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="container-custom">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="relative bg-background-900 overflow-hidden">
          <div className="absolute inset-0 opacity-15">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500 rounded-full blur-3xl"></div>
          </div>
          <div className="container-custom relative z-10 py-14 md:py-20 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              {aboutData.brand.title}
            </h1>
            <p className="text-background-300 text-sm md:text-lg max-w-2xl mx-auto">
              {aboutData.brand.subtitle}
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-custom max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  {aboutData.brand.description}
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-eye-line text-primary-500 text-xs"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-800">愿景：</span>
                      <span className="text-sm text-foreground-600">{aboutData.brand.vision}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-flag-line text-accent-500 text-xs"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-foreground-800">使命：</span>
                      <span className="text-sm text-foreground-600">{aboutData.brand.mission}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20sports%20office%20interior%20with%20glass%20walls%20and%20trophy%20displays%2C%20professional%20workspace%2C%20warm%20lighting%2C%20sports%20memorabilia%20on%20walls%2C%20collaborative%20atmosphere%2C%20clean%20aesthetic%2C%20corporate%20photography&width=700&height=500&seq=about-brand&orientation=landscape"
                  alt="bb体育品牌办公环境"
                  className="w-full h-auto object-cover"
                  title="bb体育品牌办公环境"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="container-custom max-w-5xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutData.brand.values.map((val) => (
                <div
                  key={val.title}
                  className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 text-center"
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-custom max-w-5xl">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {aboutData.reputation.title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">{aboutData.reputation.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutData.reputation.items.map((item) => (
                <div
                  key={item.title}
                  className="group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${item.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100 scroll-mt-20">
          <div className="container-custom max-w-4xl">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {aboutData.contact.title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">{aboutData.contact.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</div>
                    <a href={`mailto:${aboutData.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                      {aboutData.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">客服热线</div>
                    <a href={`tel:${aboutData.contact.phone}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                      {aboutData.contact.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">官方微信</div>
                    <span className="text-sm text-foreground-500">{aboutData.contact.wechat}</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">公司地址</div>
                    <span className="text-sm text-foreground-500">{aboutData.contact.address}</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">服务时间</div>
                    <span className="text-sm text-foreground-500">{aboutData.contact.workingHours}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden bg-background-200 h-64 md:h-full min-h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3416.86123456789!2d121.5064!3d31.2355!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35b27058b096bd21%3A0x6a5c3e1d2c789abc!2z5LiK5rW35biC5rSL5Lic5paw5Yy6!5e0!3m2!1szh-CN!2scn!4v1691000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="bb体育公司位置"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}